import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import TreeGraphBackground from "./TreeGraphBackground/index";
import { CanvasParticles } from "./CanvasParticles";
import { CampaignPopup } from "./CampaignPopup";

// DVD Bounce Button Component
const BUTTON_STAGES = [
  {
    text: "DO NOT TOUCH",
    speedMultiplier: 1,
    gradient: "from-amber-500 via-orange-500 to-red-500",
    shadow: "shadow-amber-500/30",
    scale: "scale-100",
  },
  {
    text: "NO STOP DON'T",
    speedMultiplier: 1.25,
    gradient: "from-orange-500 via-red-500 to-rose-600",
    shadow: "shadow-red-500/40",
    scale: "scale-100",
  },
  {
    text: "AAAA!!",
    speedMultiplier: 2.5,
    gradient: "from-red-600 via-rose-600 to-pink-600",
    shadow: "shadow-rose-500/50",
    scale: "scale-100",
  },
];

const DVDBounceButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const velocityRef = useRef({ x: 2, y: 1.5 });
  const baseVelocityRef = useRef({ x: 2, y: 1.5 });
  const [isVisible, setIsVisible] = useState(false);
  const [clickStage, setClickStage] = useState(0);
  const [isFrozen, setIsFrozen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleClick = () => {
    if (clickStage >= BUTTON_STAGES.length - 1) {
      setIsFrozen(true);
      onClick();
    } else {
      const nextStage = clickStage + 1;
      setClickStage(nextStage);
      const multiplier = BUTTON_STAGES[nextStage].speedMultiplier;
      // Negate X on first click, negate Y on second click
      const xSign =
        nextStage === 1
          ? -Math.sign(velocityRef.current.x)
          : Math.sign(velocityRef.current.x);
      const ySign =
        nextStage === 2
          ? -Math.sign(velocityRef.current.y)
          : Math.sign(velocityRef.current.y);
      velocityRef.current = {
        x: xSign * Math.abs(baseVelocityRef.current.x) * multiplier,
        y: ySign * Math.abs(baseVelocityRef.current.y) * multiplier,
      };
    }
  };

  useEffect(() => {
    if (!isVisible || isFrozen) return;

    const buttonWidth = 150;
    const buttonHeight = 50;
    setPosition({
      x: Math.random() * (window.innerWidth - buttonWidth - 100) + 50,
      y: Math.random() * (window.innerHeight - buttonHeight - 100) + 50,
    });

    const intervalId = setInterval(() => {
      if (!buttonRef.current) return;
      const buttonRect = buttonRef.current.getBoundingClientRect();

      setPosition((prev) => {
        let newX = prev.x + velocityRef.current.x;
        let newY = prev.y + velocityRef.current.y;

        if (newX <= 0 || newX + buttonRect.width >= window.innerWidth) {
          velocityRef.current.x *= -1;
          newX = Math.max(
            0,
            Math.min(newX, window.innerWidth - buttonRect.width)
          );
        }
        if (newY <= 0 || newY + buttonRect.height >= window.innerHeight) {
          velocityRef.current.y *= -1;
          newY = Math.max(
            0,
            Math.min(newY, window.innerHeight - buttonRect.height)
          );
        }

        return { x: newX, y: newY };
      });
    }, 16);

    return () => clearInterval(intervalId);
  }, [isVisible, isFrozen]);

  if (!isVisible) return null;

  const currentStage = BUTTON_STAGES[clickStage];

  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      className={`fixed z-50 ${currentStage.scale}`}
      style={{ left: position.x, top: position.y }}
    >
      <div
        className={`relative overflow-hidden px-6 py-3 bg-gradient-to-r ${
          isFrozen
            ? "from-gray-800 via-gray-900 to-black"
            : currentStage.gradient
        } rounded-xl shadow-lg ${
          isFrozen ? "shadow-gray-500/50" : currentStage.shadow
        } whitespace-nowrap ${isFrozen ? "border border-gray-600" : ""}`}
      >
        {!isFrozen && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12"
            animate={{
              x: ["-200%", "200%"],
            }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
          />
        )}
        <span
          className={`relative font-black text-sm tracking-wider ${
            isFrozen ? "text-4xl" : ""
          }`}
        >
          {isFrozen ? "💀" : currentStage.text}
        </span>
      </div>
    </button>
  );
};

interface IntroSplashProps {
  onContinue: () => void;
}

const HeroSection: React.FC<{ onContinue: () => void }> = ({ onContinue }) => (
  <div className="relative z-10 min-h-screen flex flex-col justify-start items-center px-4 sm:px-6 lg:px-8 pt-[17vh]">
    {/* Pre-title announcement - positioned at top of viewport */}
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="fixed top-[4vh] left-0 right-0 text-center z-20"
    >
      <span className="text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-amber-500/60">
        Live Tournaments Start Winter 2026
      </span>
    </motion.div>

    <div className="max-w-6xl mx-auto w-full flex flex-col gap-14">
      {/* Main title - add padding to maintain exact position */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, type: "spring", stiffness: 80 }}
        className="text-center pt-[calc(2.5rem+3vh)] flex flex-col gap-6"
      >
        <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black leading-[0.9] tracking-[-0.02em]">
          <motion.span
            className="inline-block text-transparent bg-clip-text bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-500 drop-shadow-[0_4px_20px_rgba(251,191,36,0.4)]"
            animate={{
              backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundSize: "200% 200%",
            }}
          >
            1V1
          </motion.span>
          <motion.span
            className="inline-block text-white ml-2 font-black drop-shadow-[0_0_40px_rgba(255,255,255,0.3)]"
            animate={{
              opacity: [0.9, 1, 0.9],
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            ME
          </motion.span>
        </h1>

        {/* Tagline - redesigned with better typography */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          <p className="text-xl sm:text-2xl md:text-3xl font-light text-gray-100 tracking-tight flex items-center justify-center gap-2">
            <span className="text-amber-400 font-bold">Create</span>
            <span className="text-gray-600">•</span>
            <span className="text-red-500 font-bold">Trade</span>
            <span className="text-gray-600">•</span>
            <span className="text-white-400 font-bold">Win</span>
          </p>
        </motion.div>
      </motion.div>

      {/* Divider line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.8, duration: 1 }}
        className="w-32 h-[1px] bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto"
      />

      <ValueProps />
      <CTASection onContinue={onContinue} />
    </div>
  </div>
);

const ValueProps: React.FC = () => {
  const valueItems = [
    {
      icon: "🎮",
      title: "CREATE & COMPETE",
      desc: "Create a new token to compete in elimination brackets running 24/7.",
    },
    {
      icon: "💰",
      title: "TRADE & GROW",
      desc: "Back your favourites in fast-paced trading wars between tokens. Winners loot losers and progress.",
    },
    {
      icon: "🏆",
      title: "WIN & LAUNCH",
      desc: "Champions launch to DEX with mega liquidity and social proof. Losers pay out prizes for deeper runs.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1, duration: 0.8 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4"
    >
      {valueItems.map((item, i) => (
        <motion.div
          key={i}
          initial={{
            opacity: 0,
            x: i === 0 ? -50 : i === 2 ? 50 : 0,
            y: i === 1 ? 50 : 0,
          }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          transition={{ delay: 1.2 + i * 0.2, duration: 0.6, type: "spring" }}
          whileHover={{
            scale: 1.05,
            rotate: [-1, 1, -1, 0],
            transition: { duration: 0.3 },
          }}
          className="relative group"
        >
          {/* Card background with gradient border effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-red-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500 opacity-0 group-hover:opacity-100" />

          <div className="relative bg-black/60 backdrop-blur-l border border-gray-800/50 rounded-3xl p-8 md:p-6 lg:p-8 flex flex-col items-center gap-3 text-center hover:border-amber-500/30 transition-all duration-500 md:min-h-[15rem]">
            {/* Icon with glow effect */}
            <div className="relative inline-block">
              <div className="text-5xl md:text-4xl lg:text-5xl">
                {item.icon}
              </div>
              <motion.div
                className="absolute inset-0 blur-2xl opacity-50"
                animate={{
                  scale: [1, 1.5, 1],
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="text-5xl md:text-4xl lg:text-5xl">
                  {item.icon}
                </div>
              </motion.div>
            </div>

            <h3 className="text-xs font-black tracking-[0.15em] text-amber-400">
              {item.title}
            </h3>
            <p className="text-m text-gray-200 leading-relaxed font-light">
              {item.desc}
            </p>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

const CTASection: React.FC<{ onContinue: () => void }> = ({ onContinue }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="flex flex-col items-center gap-2"
      >
        <motion.button
          onClick={onContinue}
          className="group relative"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          {/* Button glow background */}
          <motion.div
            className="absolute inset-0 rounded-2xl bg-gradient-to-r from-amber-600 to-red-600 blur-2xl opacity-60 group-hover:opacity-100"
            animate={{
              scale: [1, 1.2, 1],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          {/* Main button */}
          <div className="relative px-12 py-6 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl overflow-hidden">
            {/* Shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
              animate={{
                x: ["-200%", "200%"],
              }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
            />

            <span className="relative text-white font-black text-lg sm:text-xl tracking-wider flex items-center gap-3">
              ENTER THE ARENA
              <motion.span
                animate={{ x: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-2xl"
              >
                →
              </motion.span>
            </span>
          </div>
        </motion.button>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 2.2, duration: 0.8 }}
          className="text-[10px] tracking-[0.3em] text-gray-200 font-medium uppercase mt-4"
        >
          Interactive Demo
        </motion.p>

        {/* Contact Us Link */}
        <motion.a
          href="mailto:info@1v1me.me"
          className="text-white/60 hover:text-amber-400/80 transition-colors duration-300 text-sm sm:text-base font-light mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.4 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          Contact Us
        </motion.a>
      </motion.div>

      {/* DVD Bounce Waitlist Button */}
      <DVDBounceButton onClick={() => setIsPopupOpen(true)} />

      {/* Campaign Popup */}
      <CampaignPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
      />
    </>
  );
};

export const IntroSplash: React.FC<IntroSplashProps> = ({ onContinue }) => {
  return (
    <div className="min-h-screen w-full relative overflow-hidden bg-black pb-4">
      {/* Background layers with gradient */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.05)_0%,transparent_70%)]" />
        <TreeGraphBackground />
      </div>

      <HeroSection onContinue={onContinue} />
      <CanvasParticles />

      {/* Preload critical assets */}
      <div style={{ display: "none" }}>
        <img src="/catbuto2.png" alt="" />
        <video src="/catbuto2_yap.webm" muted />
        <img src="/tokens/images/shogun8.jpg" alt="" />
        <img src="/tokens/images/shogun9.jpg" alt="" />
      </div>
    </div>
  );
};
